import { useMemo } from 'react'

import type { OrganizationStatus, Permission } from '@orus.eu/right-access-management'

import { useSession } from './session.ts'

type PermissionChecker = {
  permissions: Permission[]
  hasPermission: (permission: Permission) => boolean
}

const createPermissionChecker = (rawPermissions: Permission[]): PermissionChecker => {
  const permissionsSet = new Set(rawPermissions)

  return {
    permissions: rawPermissions,
    hasPermission: (permission: Permission) => permissionsSet.has(permission),
  }
}

export const usePermissions = (): {
  type: ReturnType<typeof useSession>['permissions']['type']
  organization:
    | { technicalName: string; displayName: string; status: OrganizationStatus; isManagedByOrus: boolean }
    | undefined
} & PermissionChecker => {
  const { permissions } = useSession()

  const firstMembership = permissions.type === 'partner' ? permissions.memberships.at(0) : undefined

  const effectivePermissions = useMemo(() => {
    switch (permissions.type) {
      case 'client':
      case 'platform': {
        return permissions.rolesPermissions.flatMap(({ permissions }) => permissions)
      }
      case 'partner': {
        return firstMembership?.rolesPermissions.flatMap(({ permissions }) => permissions) ?? []
      }
    }
  }, [firstMembership, permissions])

  const permissionChecker = useMemo(() => createPermissionChecker(effectivePermissions), [effectivePermissions])

  const organizationSource = firstMembership?.organization

  const organization = useMemo(
    () =>
      organizationSource
        ? {
            technicalName: organizationSource.technicalName,
            displayName: organizationSource.displayName,
            status: organizationSource.status,
            isManagedByOrus: organizationSource.isManagedByOrus,
          }
        : undefined,
    [organizationSource],
  )

  return {
    type: permissions.type,
    ...permissionChecker,
    organization,
  }
}

type BackofficeUser = PermissionChecker &
  (
    | {
        type: 'platform'
      }
    | {
        type: 'partner'
        organization: string
        organizationDisplayName: string
      }
  )

export function useBackofficeUser(): BackofficeUser | undefined {
  const session = useSession()

  const firstMembership = session.permissions.type === 'partner' ? session.permissions.memberships.at(0) : undefined
  const effectivePermissions = useMemo(() => {
    switch (session.permissions.type) {
      case 'client':
      case 'platform': {
        return session.permissions.rolesPermissions.flatMap(({ permissions }) => permissions)
      }
      case 'partner': {
        return firstMembership?.rolesPermissions.flatMap(({ permissions }) => permissions) ?? []
      }
    }
  }, [firstMembership, session])

  const permissionChecker = useMemo(() => createPermissionChecker(effectivePermissions), [effectivePermissions])

  if (session.permissions.type === 'client') return undefined

  if (session.permissions.type === 'platform') {
    return {
      type: 'platform',
      ...permissionChecker,
    }
  }

  if (session.permissions.type === 'partner') {
    if (!firstMembership) return undefined

    return {
      type: 'partner',
      organization: firstMembership.organization.technicalName,
      organizationDisplayName: firstMembership.organization.displayName || firstMembership.organization.technicalName,
      ...permissionChecker,
    }
  }

  return undefined
}

export function usePartnerOrganization(): string | undefined {
  const backofficeUser = useBackofficeUser()
  return backofficeUser?.type === 'partner' ? backofficeUser.organization : undefined
}
